import { useState } from 'react';
import type { Position } from '@fantasy/types';
import { ApiRequestError, api, type GameweekHistory, type GameweekHistoryPlayer } from '../api.ts';

const POSITION_ORDER: Position[] = ['GKP', 'DEF', 'MID', 'FWD'];

interface SquadHistoryProps {
  squadId: string;
  /** Undefined for a hand-typed or screenshot-imported squad — there is no
   * FPL identity to read a past gameweek's picks from, so this component
   * renders nothing at all for one, same as `SquadView` gates its own
   * "Update to this gameweek" button on this field. */
  entryId: number | undefined;
  /** This squad's own current gameweek — bounds the picker to gameweeks
   * FPL has actually locked in (1 up to, not including, this one), the same
   * bound `GET /:id/history/:gameweek` itself enforces server-side. */
  currentGameweek: number;
}

/**
 * A read-only look-back at a past gameweek's picks for an FPL-linked squad
 * (T-129) — never the live squad above it, and nothing a manager could
 * save, edit or analyse from here. Collapsed by default and styled with the
 * same `panel--accent-2` teal `SquadLibrary` already uses for "saved
 * history" (see styles.css's own comment on that class), so a glance tells
 * this apart from the live pitch view rather than being mistaken for it.
 */
export function SquadHistory({ squadId, entryId, currentGameweek }: SquadHistoryProps) {
  const maxGameweek = currentGameweek - 1;
  const [gameweek, setGameweek] = useState(maxGameweek);
  const [history, setHistory] = useState<GameweekHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No FPL identity, or nothing played yet — either way this squad has no
  // past gameweek to show, so there is nothing for this panel to do.
  if (entryId === undefined || maxGameweek < 1) return null;

  async function onView() {
    setLoading(true);
    setError(null);
    try {
      const { history: loaded } = await api.getSquadHistory(squadId, gameweek);
      setHistory(loaded);
    } catch (err) {
      setHistory(null);
      setError(err instanceof ApiRequestError ? err.message : "Couldn't load that gameweek.");
    } finally {
      setLoading(false);
    }
  }

  const starting = history?.players.filter((player) => player.isStarting) ?? [];
  const bench = [...(history?.players.filter((player) => !player.isStarting) ?? [])].sort(
    (a, b) => (a.benchOrder ?? 0) - (b.benchOrder ?? 0),
  );

  return (
    <details className="panel panel--accent-2 library" aria-label="Look back at a past gameweek">
      <summary className="library-summary">
        <span>
          <span className="library-chevron" aria-hidden="true">
            ▸
          </span>
          Look back at a past gameweek
        </span>
      </summary>

      <div className="library-body">
        <p className="hint">
          What FPL recorded for this squad in a gameweek it's already played — read-only, and never
          saved as a squad of its own.
        </p>

        <div className="row mt-md">
          <div className="field">
            <label htmlFor="squad-history-gameweek">Gameweek</label>
            <input
              id="squad-history-gameweek"
              type="number"
              min={1}
              max={maxGameweek}
              value={gameweek}
              onChange={(e) => setGameweek(Number(e.target.value))}
            />
          </div>
          <button type="button" className="small" onClick={onView} disabled={loading} aria-busy={loading}>
            {loading && <span className="spinner" aria-hidden="true" />}
            {loading ? 'Loading…' : 'View'}
          </button>
        </div>

        {error && (
          <ul className="notices mt-md">
            <li className="error">{error}</li>
          </ul>
        )}

        {history && (
          <div className="mt-lg">
            <p className="hint">
              Gameweek {history.gameweek}
              {history.points !== null && <> &mdash; {history.points} points</>}
            </p>

            {POSITION_ORDER.map((position) => {
              const players = starting.filter((player) => player.position === position);
              if (players.length === 0) return null;
              return <PlayerGroup key={position} label={position} players={players} />;
            })}

            {bench.length > 0 && <PlayerGroup label="Bench" players={bench} />}
          </div>
        )}
      </div>
    </details>
  );
}

function PlayerGroup({ label, players }: { label: string; players: GameweekHistoryPlayer[] }) {
  return (
    <div className="library-group">
      <h4>{label}</h4>
      <ul className="list-reset">
        {players.map((player) => (
          <li className="library-entry" key={player.name}>
            <span>{player.name}</span>
            <span className="row row--flush">
              {player.isCaptain && <span className="pill pill--captain">C</span>}
              {player.isViceCaptain && <span className="pill pill--vice">V</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
